import { COLLISION_CELLS_PER_TILE, foldZones, frameId, propFeet, type MapDocument, type PropInstance, type ValidationIssue, type ValidationScope } from "daedalus-core";
import { AUTHORING_CELL, SIXTEENTHS } from "../movement/step.ts";
import { shrineGround, shrineStandFrom } from "../shrines/ground.ts";
import { isKnownFromStart, isTownPlace } from "../shrines/places.ts";
import { MAX_SHRINES } from "../shrines/respawn.ts";
import { isStartingShrine } from "../shrines/start.ts";
import { ZONE_RULES, ZONE_TYPES, ZoneRule } from "../zones/zones.ts";
import { SHRINE, WAYPOINT } from "./type-names.ts";

/**
 * The Issues tab's shrine checks (ADR-096 decision 41). A map holds `MAX_SHRINES` places, shrines and waypoints
 * together (ADR-137 decision 4): each past them, in the id order the map server keeps them by, is refused, since the
 * game would leave it out. A waypoint with no name is refused for the same reason. A shrine or a waypoint marked `town` that is not Known from the start is warned of
 * (ADR-137 decision 19). A shrine is warned of when it
 * has no name, a name another shrine of the map has, no `pop` strip, no painted activation cells, or a standing spot
 * that is not on a cell sheltered from players once the map's zone layers are folded, so a Hostile layer above a
 * shrine's Sanctuary is caught with unpainted ground. Every warning can be ignored, and each message names the prop,
 * then `; ` and what to do.
 *
 * A shrine's two states are not compared for equal footprints (decision 5's refusal): the map server and the page
 * stamp the state the wire gives, a shrine never leaves `available`, and so the frame `dormant` rests on reaches no
 * collision grid (ADR-096's amendment of 2026-10-05, item 5). Whether the standing spot can be walked to from the
 * spawn is a compile's check, not an edit's (`shrine-reach.ts`).
 */

/**
 * The animation a shrine's pop plays, as its type's transitions name it.
 */
const POP = "pop";

/**
 * A shrine or a waypoint of a map: its layer, its prop and its name as the game reads it, trimmed.
 */
interface PlacedShrine {
  readonly layerId: string;
  readonly prop: PropInstance;
  readonly name: string;
}

/**
 * Whether a placed place is a waypoint, which has no respawn list, pop strip or Sanctuary of its own to check.
 *
 * @param place The place.
 * @returns True for a waypoint.
 */
function isWaypoint(place: PlacedShrine): boolean {
  return place.prop.behaviour?.type === WAYPOINT;
}

/**
 * Every Shrine and Waypoint prop of a map, layer by layer in document order.
 *
 * @param map The map.
 * @returns The places.
 */
function shrinesOf(map: MapDocument): PlacedShrine[] {
  const shrines: PlacedShrine[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      if (prop.behaviour?.type === SHRINE || prop.behaviour?.type === WAYPOINT) {
        const name = prop.behaviour.fields["name"];
        shrines.push({ layerId: layer.id, prop, name: typeof name === "string" ? name.trim() : "" });
      }
    }
  }
  return shrines;
}

/**
 * Whether a place is marked a town's without being Known from the start.
 *
 * @param place The place.
 * @returns True for `town` ticked and neither `knownFromStart` nor `start`.
 */
function isTownUnknown(place: PlacedShrine): boolean {
  const fields = place.prop.behaviour?.fields;
  return isTownPlace(fields) && !isKnownFromStart(fields) && !isStartingShrine(fields);
}

/**
 * A warning on one shrine that its builder may ignore.
 *
 * @param map The map.
 * @param shrine The shrine.
 * @param kind The check's kind.
 * @param message What is wrong, then `; ` and what to do.
 * @returns The issue.
 */
function warning(map: MapDocument, shrine: PlacedShrine, kind: string, message: string): ValidationIssue {
  return { mapId: map.id, kind, layerId: shrine.layerId, propId: shrine.prop.id, severity: "warning", ignorable: true, message };
}

/**
 * The collision cell whose zone the map server reads for a character standing up at a shrine: the cell holding the
 * point its search for a standing spot starts from (`shrineStandFrom`), under the stone's base.
 *
 * @param prop The shrine.
 * @param scope The world's tile size and compile context.
 * @returns The cell's column and row, or null when the context holds no footprint for the placed frame, so where the
 *   stone stands in it is unknown.
 */
function standingCell(prop: PropInstance, scope: ValidationScope): { column: number; row: number } | null {
  const footprint = scope.context.collision.footprints?.[frameId(prop.frame.tileset, null, prop.frame.tile)];
  if (footprint === undefined) {
    return null;
  }
  const feet = propFeet(prop, footprint.w, scope.tileSize);
  const ground = shrineGround({ x: feet.x * SIXTEENTHS, y: feet.y * SIXTEENTHS, footprint, flipX: prop.flipX, flipY: prop.flipY }, scope.tileSize);
  const stand = shrineStandFrom(ground.x, ground.y);
  return { column: Math.floor(stand.x / AUTHORING_CELL), row: Math.floor(stand.y / AUTHORING_CELL) };
}

/**
 * The warnings of each shrine's standing spot that is not sheltered from players on the folded zone grid.
 *
 * @param map The map.
 * @param shrines Its shrines.
 * @param scope The world's tile size and compile context.
 * @returns One warning per such shrine, naming the ground its spot is on.
 */
function safeZoneIssues(map: MapDocument, shrines: readonly PlacedShrine[], scope: ValidationScope): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const folded = shrines.length === 0 ? null : foldZones(map);
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const height = map.height * COLLISION_CELLS_PER_TILE;
  for (const shrine of shrines) {
    const cell = standingCell(shrine.prop, scope);
    if (cell === null || folded === null) {
      continue;
    }
    const inside = cell.column >= 0 && cell.row >= 0 && cell.column < width && cell.row < height;
    const held = inside ? (folded.cells[cell.row * width + cell.column] as number) : 0;
    const type = held === 0 ? null : (folded.types[held - 1] as string);
    if (type !== null && ((ZONE_RULES[type] ?? 0) & ZoneRule.NoPvp) !== 0) {
      continue;
    }
    const label = type === null ? null : (ZONE_TYPES.find((candidate) => candidate.id === type)?.label ?? type);
    const fix = label === null ? "press Paint its Sanctuary zone on the Behaviour tab, or paint Sanctuary cells around it" : `move its Sanctuary cells to a zone layer above the ${label} one, or paint Sanctuary cells around it on the top zone layer`;
    issues.push(warning(map, shrine, "shrine.no-safe-zone", `prop ${shrine.prop.id} is a Shrine whose standing spot is on ${label ?? "unpainted"} ground, where a character who stands up can be attacked; ${fix}`));
  }
  return issues;
}

/**
 * The checks of one shrine alone: its name, its `pop` strip and its painted cells.
 *
 * @param map The map.
 * @param shrine The shrine.
 * @returns The warnings, in that order.
 */
function ownIssues(map: MapDocument, shrine: PlacedShrine): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const id = shrine.prop.id;
  if (shrine.name === "") {
    issues.push(warning(map, shrine, "shrine.no-name", `prop ${id} is a Shrine with no name, so the game calls it "the shrine"; type its Name on the Behaviour tab`));
  }
  if (shrine.prop.behaviour?.animations?.[POP] === undefined) {
    issues.push(warning(map, shrine, "shrine.no-pop", `prop ${id} is a Shrine with no pop strip, so it cuts when a character arrives; choose the strip its pop plays under Transitions on the Behaviour tab`));
  }
  if ((shrine.prop.behaviour?.activation ?? []).length === 0) {
    issues.push(warning(map, shrine, "shrine.no-ring", `prop ${id} is a Shrine with no activation cells painted, so it is discovered only by walking up to the stone; press Paint its Sanctuary zone on the Behaviour tab, or paint its Activation cells`));
  }
  return issues;
}

/**
 * The shrine checks of one map.
 *
 * @param map The map.
 * @param scope The world's tile size and compile context, which the standing spot is read with; without one that
 *   check is left out.
 * @returns Each shrine past `MAX_SHRINES` refused, then per shrine in document order its own warnings, a repeated
 *   name and a standing spot off safe ground.
 */
export function shrineIssues(map: MapDocument, scope?: ValidationScope): ValidationIssue[] {
  const shrines = shrinesOf(map);
  if (shrines.length === 0) {
    return [];
  }
  const kept = new Set([...shrines.map((shrine) => shrine.prop.id)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).slice(0, MAX_SHRINES));
  const issues: ValidationIssue[] = shrines.filter((shrine) => !kept.has(shrine.prop.id)).map((shrine): ValidationIssue => ({ mapId: map.id, kind: "shrine.too-many", layerId: shrine.layerId, propId: shrine.prop.id, message: `prop ${shrine.prop.id} is one ${isWaypoint(shrine) ? "waypoint" : "shrine"} more than the ${MAX_SHRINES} shrines and waypoints a map holds, and the game would leave it out; delete a shrine or a waypoint of this map` }));
  const unsafe = scope === undefined ? [] : safeZoneIssues(map, shrines.filter((shrine) => !isWaypoint(shrine)), scope);
  for (const shrine of shrines) {
    if (isTownUnknown(shrine)) {
      issues.push(warning(map, shrine, "place.town-unknown", `prop ${shrine.prop.id} is a town place that is not Known from the start, so a trip to it costs coin from a shrine until it is discovered; tick Known from the start on the Behaviour tab, or untick Town`));
    }
    if (isWaypoint(shrine)) {
      if (shrine.name === "") {
        issues.push({ mapId: map.id, kind: "waypoint.no-name", layerId: shrine.layerId, propId: shrine.prop.id, message: `prop ${shrine.prop.id} is a Waypoint with no name, and the game leaves it out; type its Name on the Behaviour tab` });
      }
      if (shrine.prop.behaviour?.animations?.[POP] === undefined) {
        issues.push(warning(map, shrine, "waypoint.no-pop", `prop ${shrine.prop.id} is a Waypoint with no pop strip, so it cuts when it is discovered; choose the strip its pop plays under Transitions on the Behaviour tab`));
      }
      continue;
    }
    issues.push(...ownIssues(map, shrine));
    const twin = shrine.name === "" ? undefined : shrines.find((other) => other !== shrine && !isWaypoint(other) && other.name.toLowerCase() === shrine.name.toLowerCase());
    if (twin !== undefined) {
      issues.push(warning(map, shrine, "shrine.name-twice", `prop ${shrine.prop.id} and prop ${twin.prop.id} are both Shrines named ${JSON.stringify(shrine.name)}, so a player cannot tell them apart on the respawn list; rename one of them`));
    }
    issues.push(...unsafe.filter((issue) => issue.propId === shrine.prop.id && issue.layerId === shrine.layerId));
  }
  return issues;
}
